import type { AppProps } from 'next/app';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { ModuleRegistry as AgChartsModuleRegistry } from 'ag-charts-community';
import { AgChartsEnterpriseModule, AllEnterpriseModule as AgChartsAllEnterpriseModule } from 'ag-charts-enterprise';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import Head from 'next/head';
import { env } from 'next-runtime-env';
import { useRouter } from 'next/router';
import { type ReactElement, type ReactNode, useEffect, useCallback, useState, useMemo } from 'react';
import initUnocssRuntime from '@unocss/runtime';
import presetWind3 from '@unocss/preset-wind3';
import { unoRuntimeConfig } from '@/config/unocss-runtime.config';
import SessionTimeoutAlertWrapper from '@/components/Account/SessionTimeoutAlert/SessionTimeoutAlertWrapper';
import DatadogInit from '@/components/DataDog/DataDogInit';
import { AppProvider as ServiceAppProvider } from '@/contexts/AppProvider';
import '@/styles/globals.css';
import {
  preloadFederationConfig,
  prefetchRemoteEntry,
  getAvailableRemotes,
  getFederationFeatures,
} from '@/lib/moduleFederation/config/federation';
import { manifestLoader } from '@/lib/moduleFederation/config/manifestLoader';
import { loadFederatedStyles } from '@/lib/styles/loadFederatedStyles';
import AppProvider from '@/providers/AppProvider/AppProvider';
import QueryProvider from '@/providers/QueryProvider/QueryProvider';
import { UnoCSSReadyGate } from '@/components/UnoCSSReadyGate';

// AG Grid modules (grid + integrated charts)
ModuleRegistry.registerModules([AllCommunityModule]);
ModuleRegistry.registerModules([AllEnterpriseModule.with(AgChartsEnterpriseModule)]);

// AG Charts standalone modules (for <AgCharts> React component via ag-charts-react)
AgChartsModuleRegistry.registerModules([AgChartsAllEnterpriseModule]);

LicenseManager.setLicenseKey(env('NEXT_PUBLIC_AG_GRID_KEY') || '');

// Guard to prevent multiple UnoCSS initializations
let unoCSSInitialized = false;

export type NextPageWithLayout<P = {}, IP = P> = React.ComponentType<P> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

export type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function AppContent({
  Component,
  pageProps,
  getLayout,
}: {
  Component: NextPageWithLayout;
  pageProps: any;
  getLayout: (page: ReactElement) => ReactNode;
}) {
  const router = useRouter();
  /** Actual URL path (Pages Router `pathname` is the route pattern, e.g. `/facility/[id]`). */
  const pathnameForNavigation = useMemo(() => {
    if (!router.isReady) {
      return router.pathname;
    }
    const withoutQuery = router.asPath.split('?')[0]?.split('#')[0];
    return withoutQuery && withoutQuery.length > 0 ? withoutQuery : router.pathname;
  }, [router.isReady, router.asPath, router.pathname]);

  // Navigation handlers for federated components - memoized to prevent re-renders
  const handleNavigate = useCallback(
    (path: string) => {
      // Handle special dealcard routing for federated components
      if (path.startsWith('/dealcard/')) {
        const id = path.split('/')[2];
        router.push(`/securitization/dealcard/${id}`);
      } else if (path === '/dealcard') {
        router.push('/securitization/dealcard');
      } else if (path.startsWith('/investor/')) {
        router.push(`/securitization${path}`);
      } else if (path === '/investor') {
        router.push('/securitization/investor');
      } else if (path === '/sales') {
        router.push('/securitization/sales');
      } else {
        router.push(path);
      }
    },
    [router],
  );

  const handleGoBack = useCallback(() => {
    router.back();
  }, [router]);

  const handleReplace = useCallback(
    (path: string) => {
      router.replace(path);
    },
    [router],
  );

  const handlePrefetch = useCallback(
    (path: string) => {
      router.prefetch(path);
    },
    [router],
  );

  return (
    <div>
      <DatadogInit />
      <ServiceAppProvider
        onNavigate={handleNavigate}
        onGoBack={handleGoBack}
        onReplace={handleReplace}
        onPrefetch={handlePrefetch}
        pathname={pathnameForNavigation}
        query={router.query as Record<string, any>}
        asPath={router.asPath}>
        <AppProvider>
          <QueryProvider>
            {getLayout(<Component {...pageProps} />)}
            <SessionTimeoutAlertWrapper />
          </QueryProvider>
        </AppProvider>
      </ServiceAppProvider>
    </div>
  );
}

function App({ Component, pageProps }: AppPropsWithLayout) {
  const getLayout = Component.getLayout ?? (page => page);

  const [externalStylesLoaded, setExternalStylesLoaded] = useState(false);
  const [unoCSSReady, setUnoCSSReady] = useState(false);
  const [remotesReady, setRemotesReady] = useState(false);

  // Load federated styles (runs outside the gate so it's never blocked)
  useEffect(() => {
    const loadStyles = async () => {
      try {
        await preloadFederationConfig();

        const features = await getFederationFeatures();
        const styleLoaderRemote = features['style-loader-remote'] || 'aga-components';

        const { federationConfigLoader, resolveRemoteUrl } = await import(
          '@/lib/moduleFederation/config/federationConfigLoader'
        );
        const remoteConfig = federationConfigLoader.getRemoteConfig(styleLoaderRemote);

        if (remoteConfig) {
          const { url } = resolveRemoteUrl(remoteConfig);
          const baseUrl = url.replace('/remoteEntry.js', '').replace(/\/$/, '');

          await loadFederatedStyles({
            baseUrl,
            preserveOrder: true,
            onStylesLoaded: (count: number) => {
              console.log(`[App] Loaded ${count} CSS files`);
              setExternalStylesLoaded(true);
            },
          });
        } else {
          console.warn(`[App] ${styleLoaderRemote} remote config not found, styles may not load`);
          setExternalStylesLoaded(true);
        }
      } catch (error) {
        console.error('[App] Failed to load federated styles:', error);
        setExternalStylesLoaded(true);
      }
    };

    loadStyles();
  }, []);

  // Prefetch non-lazy remotes and module manifests before revealing the app shell
  useEffect(() => {
    const preloadNonLazyRemotes = async () => {
      try {
        await preloadFederationConfig();
        const { remotes } = await getAvailableRemotes();

        const prefetches = Object.entries(remotes)
          .filter(([, config]) => config.lazyLoad === false)
          .map(async ([name, config]) => {
            console.log(`[App] Preloading ${name} (lazyLoad: false)`);
            await prefetchRemoteEntry(config);
          });

        await Promise.all([
          ...prefetches,
          manifestLoader.loadAllManifests().catch(err => {
            console.warn('[App] Failed to load module manifests:', err);
          }),
        ]);
      } catch (error) {
        console.error('[App] Failed to preload remotes:', error);
      } finally {
        setRemotesReady(true);
      }
    };

    preloadNonLazyRemotes();
  }, []);

  // Initialize UnoCSS runtime AFTER external stylesheets are loaded
  // Note: unoRuntimeConfig doesn't import from 'unocss' to avoid Node.js dependencies
  // Presets must be imported from esm.sh for browser compatibility
  useEffect(() => {
    // Wait for external stylesheets to load first
    if (!externalStylesLoaded) {
      return;
    }

    // Guard: Check if already initialized
    if (unoCSSInitialized) {
      console.log('[UnoCSS] Already initialized, skipping...');
      setUnoCSSReady(true);
      return;
    }

    // Check if UnoCSS runtime already exists in the DOM or on window
    if (typeof window !== 'undefined') {
      // Check for global UnoCSS runtime object
      const hasUnocssRuntime =
        !!(window as any).__unocss || !!(window as any).UnoCSS || !!(window as any).__unocss_runtime;
      if (hasUnocssRuntime) {
        console.log('[UnoCSS] UnoCSS runtime already detected on window, skipping initialization');
        unoCSSInitialized = true;
        setUnoCSSReady(true);
        return;
      }

      // Check if UnoCSS style tags already exist in the DOM
      const existingStyles = document.querySelectorAll('style[data-unocss-runtime-layer]');
      if (existingStyles.length > 0) {
        console.log('[UnoCSS] UnoCSS styles already detected, skipping initialization');
        unoCSSInitialized = true;
        setUnoCSSReady(true);
        return;
      }
    }

    // Set flag immediately to prevent race conditions with React Strict Mode
    unoCSSInitialized = true;

    const initializeUnoCSS = async () => {
      console.log('[UnoCSS] Starting UnoCSS runtime initialization...');
      console.log('[UnoCSS] Import check:', {
        hasInitUnocssRuntime: typeof initUnocssRuntime === 'function',
        initUnocssRuntimeType: typeof initUnocssRuntime,
        initUnocssRuntimeName: initUnocssRuntime?.name,
      });
      console.log('[UnoCSS] Config:', {
        hasBlocklist: !!unoRuntimeConfig.blocklist,
        blocklistLength: unoRuntimeConfig.blocklist?.length || 0,
        hasTheme: !!unoRuntimeConfig.theme,
        themeColors: Object.keys(unoRuntimeConfig.theme?.colors || {}).length,
        themeWidths: Object.keys(unoRuntimeConfig.theme?.width || {}).length,
        customWidths: Object.keys(unoRuntimeConfig.theme?.width || {}),
      });

      try {
        if (typeof initUnocssRuntime !== 'function') {
          throw new Error('initUnocssRuntime is not a function');
        }

        console.log('[UnoCSS] Preset check:', {
          hasPresetWind3: typeof presetWind3 === 'function',
          presetWind3Type: typeof presetWind3,
        });

        // Create config with presets
        const configWithPresets = {
          ...unoRuntimeConfig,
          presets: [presetWind3()],
        };

        console.log('[UnoCSS] Initializing with presets:', {
          presetCount: configWithPresets.presets?.length || 0,
        });

        const result = initUnocssRuntime({ defaults: configWithPresets });
        // initUnocssRuntime may return a Promise or void
        const resolvedResult = result instanceof Promise ? await result : result;
        setUnoCSSReady(true);
        console.log('[UnoCSS] ✅ Runtime initialized successfully', {
          result: resolvedResult,
          resultType: typeof resolvedResult,
          resultKeys:
            resolvedResult !== null && resolvedResult !== undefined && typeof resolvedResult === 'object'
              ? Object.keys(resolvedResult)
              : null,
        });

        // Move UnoCSS style tags to the end of head to ensure they override external stylesheets
        if (typeof window !== 'undefined') {
          // Wait a bit for style tags to be created
          setTimeout(() => {
            const unocssStyleTags = document.querySelectorAll('style[data-unocss-runtime-layer]');
            unocssStyleTags.forEach(styleTag => {
              // Move to end of head to ensure cascade order (last styles win)
              document.head.appendChild(styleTag);
            });
            if (unocssStyleTags.length > 0) {
              console.log(
                `[UnoCSS] Moved ${unocssStyleTags.length} style tag(s) to end of head for proper cascade order`,
              );
            }
          }, 100);
        }

        // Check if UnoCSS runtime is available on window
        if (typeof window !== 'undefined') {
          // @ts-ignore - UnoCSS runtime may expose global
          const unocssGlobal = (window as any).__unocss || (window as any).UnoCSS;
          console.log('[UnoCSS] Global runtime check:', {
            hasWindowUnocss: !!(window as any).__unocss,
            hasWindowUnoCSS: !!(window as any).UnoCSS,
            unocssGlobal,
          });

          // Check if styles are being generated
          setTimeout(() => {
            const styleSheets = Array.from(document.styleSheets);
            const unocssStyles = styleSheets.filter(sheet => {
              try {
                return sheet.href?.includes('unocss') || sheet.ownerNode?.textContent?.includes('unocss');
              } catch {
                return false;
              }
            });
            console.log('[UnoCSS] Style sheets check:', {
              totalSheets: styleSheets.length,
              unocssSheets: unocssStyles.length,
              allSheets: styleSheets.map(s => ({
                href: s.href,
                disabled: s.disabled,
              })),
            });

            // Check for UnoCSS-generated styles in the DOM
            const styleTags = Array.from(document.querySelectorAll('style'));
            const unocssStyleTags = styleTags.filter(tag => {
              const text = tag.textContent || '';
              return text.includes('unocss') || text.match(/\.(?:bg-|text-|p-|m-|w-|h-)/);
            });
            console.log('[UnoCSS] Style tags check:', {
              totalStyleTags: styleTags.length,
              potentialUnocssTags: unocssStyleTags.length,
              unocssTagContents: unocssStyleTags.map(t => t.textContent?.substring(0, 100)),
            });

            // Test if UnoCSS is processing classes by checking for common Tailwind classes
            const testElements = document.querySelectorAll(
              '[class*="bg-"], [class*="text-"], [class*="p-"], [class*="m-"], [class*="w-"], [class*="h-"]',
            );
            console.log('[UnoCSS] Elements with Tailwind-like classes:', {
              count: testElements.length,
              sampleClasses: Array.from(testElements)
                .slice(0, 5)
                .map(el => el.className),
            });

            // Check for custom theme classes that are critical for layout
            const customWidthElements = document.querySelectorAll(
              '[class*="w-main"], [class*="w-sidebar"], [class*="min-w-sidebar"]',
            );
            console.log('[UnoCSS] Elements with custom width classes (critical for layout):', {
              count: customWidthElements.length,
              classes: Array.from(customWidthElements).map(el => ({
                className: el.className,
                computedWidth: window.getComputedStyle(el as Element).width,
              })),
            });

            // Check if custom width classes are in the generated styles
            const allStyles = Array.from(document.querySelectorAll('style'))
              .map(s => s.textContent || '')
              .join('\n');
            const hasCustomWidths = /\.w-main|\.w-sidebar|\.min-w-sidebar/.test(allStyles);
            console.log('[UnoCSS] Custom width classes in generated styles:', {
              found: hasCustomWidths,
              sample: allStyles.match(/\.w-main[^{]*\{[^}]*\}/g)?.slice(0, 3),
            });

            // Check if UnoCSS runtime is watching DOM changes
            // @ts-ignore
            if ((window as any).__unocss_runtime) {
              console.log('[UnoCSS] ✅ Runtime watcher detected on window.__unocss_runtime');
            } else {
              console.warn('[UnoCSS] ⚠️ Runtime watcher not found on window.__unocss_runtime');
            }

            // Test UnoCSS by creating a test element with a custom class
            const testDiv = document.createElement('div');
            testDiv.className = 'w-main-sm bg-primary text-primary-contrast p-4';
            testDiv.style.position = 'absolute';
            testDiv.style.top = '-9999px';
            testDiv.style.left = '-9999px';
            document.body.appendChild(testDiv);

            setTimeout(() => {
              const computedStyle = window.getComputedStyle(testDiv);
              const hasWidth = computedStyle.width && computedStyle.width !== 'auto' && computedStyle.width !== '0px';
              const hasBgColor = computedStyle.backgroundColor && computedStyle.backgroundColor !== 'rgba(0, 0, 0, 0)';
              console.log('[UnoCSS] Test element style generation:', {
                className: testDiv.className,
                computedWidth: computedStyle.width,
                computedBgColor: computedStyle.backgroundColor,
                hasWidth: hasWidth,
                hasBgColor: hasBgColor,
                widthMatchesExpected:
                  computedStyle.width === 'calc(100% - 256px)' || computedStyle.width.includes('calc'),
              });
              document.body.removeChild(testDiv);
            }, 500);
          }, 1000);
        }
      } catch (error) {
        console.error('[UnoCSS] ❌ Runtime initialization failed:', error);
        console.error('[UnoCSS] Error details:', {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });
        setUnoCSSReady(true); // Reveal on error to avoid permanent blank screen
      }
    };

    initializeUnoCSS();
  }, [externalStylesLoaded]);

  return (
    <>
      <Head>
        <title>AGA Funding Portal</title>
        <meta name="description" content="AGA Capital funding portal" />
      </Head>
      <UnoCSSReadyGate isReady={unoCSSReady && remotesReady}>
        <AppContent Component={Component} pageProps={pageProps} getLayout={getLayout} />
      </UnoCSSReadyGate>
    </>
  );
}

export default App;
